// Minimal GraphQL client using fetch. Sends cookies (session) via `credentials: 'include'`.
// Keeps surface area small; no codegen / no query string interpolation helpers.

import { createElement, createContext, useContext, type ReactNode } from 'react';

export class GraphQLClientError extends Error {
  constructor(
    message: string,
    readonly errors: Array<{ message: string; path?: (string | number)[]; extensions?: Record<string, unknown> }>,
  ) {
    super(message);
    this.name = 'GraphQLClientError';
  }
}

export interface GqlClientOptions {
  /** Optional function that returns a Bearer token for the Authorization header. */
  getToken?: () => string | null;
}

export function createGqlClient(endpoint: string, opts?: GqlClientOptions) {
  async function request<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = opts?.getToken?.();
    if (token) headers['authorization'] = `Bearer ${token}`;

    const res = await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ query, variables }),
    });
    const body = (await res.json()) as { data?: T; errors?: Array<{ message: string; path?: (string | number)[]; extensions?: Record<string, unknown> }> };
    if (body.errors && body.errors.length > 0) {
      throw new GraphQLClientError(body.errors.map((e) => e.message).join('; '), body.errors);
    }
    if (!res.ok) throw new Error(`GraphQL HTTP ${res.status}`);
    if (!body.data) throw new Error('GraphQL response missing data');
    return body.data;
  }

  /**
   * SSE-based GraphQL subscription. Works with GraphQL Yoga's built-in
   * Server-Sent Events transport. Returns an unsubscribe function.
   */
  function subscribe<T>(
    query: string,
    variables: Record<string, unknown>,
    onData: (data: T) => void,
  ): () => void {
    const controller = new AbortController();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    const token = opts?.getToken?.();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const raw = line.slice(6).trim();
              if (!raw) continue;
              try {
                const json = JSON.parse(raw) as { data?: T };
                if (json.data) onData(json.data);
              } catch { /* skip malformed */ }
            }
          }
        }
      })
      .catch(() => { /* aborted or network error */ });

    return () => controller.abort();
  }

  return { request, subscribe };
}

export type GqlClient = ReturnType<typeof createGqlClient>;

// ─── React context for module components ──────────────────────────────
const GqlContext = createContext<GqlClient | null>(null);

export function GqlProvider({ client, children }: { client: GqlClient; children: ReactNode }) {
  return createElement(GqlContext.Provider, { value: client }, children);
}

export function useGql(): GqlClient {
  const client = useContext(GqlContext);
  if (!client) throw new Error('useGql must be used within a GqlProvider');
  return client;
}
